<script lang="ts">
export default {
  name: 'CompaniesAnalysisScatterChart',
}
</script>
<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import ChartScatter from '../../../../components/domains/analysis/scatter/ChartScatter.vue'
import type { RawAgg, PageType } from '../../../../stores/companies/analysis/scatter'
import type { Chart, ChartType, TooltipModel } from 'chart.js'

const route = useRoute()

interface Context<TType extends ChartType> {
  chart: Chart
  tooltip: TooltipModel<TType>
}
interface Field {
  id: string
  key: string
  name: string
  uuid: string
  value: number
}
interface ScatterData {
  x: number
  y: number
  field: Field
}

interface Props {
  rawAggs: RawAgg[]
  pageType: PageType,
  showLabel: boolean
}
const props = withDefaults(defineProps<Props>(), {
  rawAggs: () => [],
})

const currentCompanyUuid = computed<string>(() => route.params.company_uuid as string)

const adaptKey = (key: string): string => {
  switch(key) {
  case('store_id'):
    return 'stores'
  case('site_type'):
    return 'sites'
  case('brand_id'):
    return 'brands'
  case('franchisee_id'):
    return 'franchisees'
  case('user_company_id'):
    return 'user_companies'
  case('prefecture'):
    return 'prefectures'
  case('area_id'):
    return 'areas'
  case('original_tag_group_a_tag_id'):
    return 'original_tag_group_a'
  case('original_tag_group_b_tag_id'):
    return 'original_tag_group_b'
  case('original_tag_group_c_tag_id'):
    return 'original_tag_group_c'
  case('original_tag_group_d_tag_id'):
    return 'original_tag_group_d'
  case('original_tag_group_e_tag_id'):
    return 'original_tag_group_e'
  default:
    return key
  }
}
const adaptId = (field: Field): string => {
  switch(field.key) {
  case('store_id'):
    return field.uuid
  default:
    return field.id
  }
}

const chartOptions = computed(() => {
  const x = {
    beginAtZero: true,
    max: 0
  }
  const y = {
    beginAtZero: true,
    max: 5.2,
    min: 1,
    ticks: {
      callback: function (value: number) {
        if (value == y.max) return ''
        return `★${value.toFixed(1)}`
      }
    }
  }
  /**
   * Y軸（口コミ数）の最大値の仕様
   * 1.y軸の最大値は最大件数 + 最大件数の5%
   * 2.ただし、最大件数の5%の値が1未満の場合は最大件数を最大値とする
   *  - 最大件数が10の場合 → 最大値は10
   *  - 最大件数が20の場合 → 最大値は21
   * 3.y軸の最大値が３桁以上の場合、１の位を切り上げ
   *  - 最大値が129の場合130にする
   *  - 最大値が1489の場合、1490にする
   */
  const countMax = Math.max(...props.rawAggs.filter((v: RawAgg) => !!v.scores).map((v: RawAgg) => v.counts))
  if (countMax * 0.05 < 1) {
    x.max = countMax
  } else {
    x.max = countMax * 1.05
  }
  if (x.max >= 100) {
    x.max = Math.ceil( x.max / 10) * 10
  }
  x.max = Math.round(x.max)

  const legend = { display: false }
  const layout = {}
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x,
      y,
    },
    layout,
    events: ['mousemove', 'click', 'touchstart', 'touchmove'], // 対象の点から離れた際に、ツールチップが消えないようにする
    plugins: {
      legend,
      tooltip: {
        // キャンバス上のツールチップを無効にする
        enabled: false,

        external: (context: Context<'scatter'>) => {
          customTooltip(context)
        },
      },
      datalabels: {
        display: props.showLabel,
        anchor: 'end',
        align: 'end',
        offset: 5,
        color: 'rgba(0, 0, 0, 0.8)',
        font: {
          size: 10
        },
        formatter: function (value: ScatterData) {
          return value.field.name
        },
      }
    }
  }
})
/**
 * 散布図上の点にホバーした際に出現させるカスタムツールチップ
 * ツールチップ内にリンクを実装したかったので直接DOMを生成している
 */
const customTooltip = (context: Context<'scatter'>): void => {
  let tooltipEl = document.getElementById('chartjs-tooltip')

  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.id = 'chartjs-tooltip'
    tooltipEl.innerHTML =
            '<div style="position: relative; font-family: Hiragino Kaku Gothic Pro; font-size: 10px;">' +
            '<div style="position: absolute; top: 35px; left: -14px; margin-top: -12px; border: 6px solid transparent; border-right: 8px solid rgba(0, 0, 0, 0.8);">' +
            '</div>' +
            '<table style="background-color: rgba(0, 0, 0, 0.8); padding: 4px 12px; border-radius: 8px; border-spacing: 0 8px;"></table></div>'
    document.body.appendChild(tooltipEl)
  }

  const tooltipModel = context.tooltip
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.display = 'none'
    return
  }

  tooltipEl.classList.remove('above', 'below', 'no-transform')
  tooltipEl.classList.add(tooltipModel.yAlign ? tooltipModel.yAlign : 'no-transform')

  let innerHtml = '<thead>'
  innerHtml += '</thead><tbody style="color: white;">'

  tooltipModel.dataPoints.forEach(dataPoint => {
    const scatterData: ScatterData = dataPoint.raw as ScatterData
    const link = '<a style="cursor: pointer; color: white;" href="' + `/s/companies/${currentCompanyUuid.value}/reviews?analysis_type=${adaptKey(scatterData.field.key)}&analysis_id=${adaptId(scatterData.field)}` + '" >' + '口コミを見る' + '</a>'

    innerHtml += '<tr style="margin-bottom: 15px;"><td>' + scatterData.field.name + '</br>' + '評価: ★' + scatterData.y + '</br>' + '件数: ' + scatterData.x + '件' + '</br>' + link + '</td></tr>'
  })
  innerHtml += '</tbody>'

  let tableRoot = tooltipEl.querySelector('table')
  if (tableRoot) tableRoot.innerHTML = innerHtml

  const position = context.chart.canvas.getBoundingClientRect()

  tooltipEl.style.display = 'block'
  tooltipEl.style.position = 'absolute'
  tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 15 + 'px'
  tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - 30 + 'px'
}

const chartData = computed(() => {
  const dataArray = []

  /**
   * 分析軸が「店舗別」の場合は
   * 絞り込み条件で絞り込まれた店舗idを利用して表示する店舗をフィルタリング
   */
  for (let agg of props.rawAggs) {
    dataArray.push({ x: agg.counts, y: agg.scores, field: agg.fields[0] })
  }
  return {
    datasets: [{
      data: dataArray,
      backgroundColor: 'rgba(233, 68, 106, 0.3)'
    }]
  }
})

</script>
<template>
  <ChartScatter
    class="p-lineChart__container"
    :chart-data="chartData"
    :options="chartOptions"
    :height="500"
  />
</template>
<style lang="scss" scoped>
</style>
